import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  RESOURCE_KEY,
  ResourceKey,
  OFFICE_HOURS_SUBSESSION_IDS,
  LubavOfficeHoursSubsessionType,
  OFFICE_HOURS_SESSION_IDS,
  getNuggetOutputResources,
} from '@backrs_ui/grow-ui/src/models/models';

const { officeHours } = appStrings.modules;

export const BOX_ORDER = [
  'FEATURED',
  'TOOLS',
  'COACHES',
  'INTERNSHIPS',
  'CAREER_PATHS',
  'SCHOLARSHIPS',
  'EMPLOYERS',
  'INDUSTRIES',
  'COLLEGES',
  'MAJORS',
  'LEARNING',
  'RESOURCES',
  'OPPORTUNITIES',
  'PROFILES',
  'DEEP_DIVES',
  'SUGGESTED',
] as const;

export type BoxType = (typeof BOX_ORDER)[number];

export const CATEGORY_TITLE_MAP: Record<BoxType, string> = {
  FEATURED: officeHours.categories.featured,
  SUGGESTED: officeHours.categories.suggested,
  // legacy tabs for supercoach
  RESOURCES: officeHours.categories.resources,
  OPPORTUNITIES: officeHours.categories.opportunities,
  PROFILES: officeHours.categories.profiles,
  // tabs for office hours
  MAJORS: officeHours.categories.majors,
  TOOLS: officeHours.categories.tools,
  LEARNING: officeHours.categories.learning,
  CAREER_PATHS: officeHours.categories.career_paths,
  INTERNSHIPS: officeHours.categories.internships,
  SCHOLARSHIPS: officeHours.categories.scholarships,
  EMPLOYERS: officeHours.categories.employers,
  COLLEGES: officeHours.categories.colleges,
  INDUSTRIES: officeHours.categories.industries,
  COACHES: officeHours.categories.coaches,
  DEEP_DIVES: officeHours.categories.deep_dives,
} as const;

export type InfoBoxConfig = Readonly<{
  title: string;
  resources: ResourceKey[];
}>;

export type InfoBoxConfigMap = Partial<Record<BoxType, InfoBoxConfig>>;

type SubsessionConfig = Partial<Record<BoxType, ResourceKey[]>>;

export const ALL_CONTENT_RESOURCE_KEYS: ResourceKey[] = [
  RESOURCE_KEY.TOOLS,
  RESOURCE_KEY.SUGGESTED_PLAYBOOKS,
  RESOURCE_KEY.COMPETENCIES,
  RESOURCE_KEY.INTERNSHIPS,
  RESOURCE_KEY.CAREER_PATHS,
  RESOURCE_KEY.SCHOLARSHIPS,
  RESOURCE_KEY.EMPLOYERS,
  RESOURCE_KEY.INDUSTRIES,
  RESOURCE_KEY.COLLEGES,
  RESOURCE_KEY.MAJORS,
  RESOURCE_KEY.STORIES,
  RESOURCE_KEY.LEARNING,
  RESOURCE_KEY.COACHES,
  RESOURCE_KEY.DEEP_DIVES,
];

// base ordering/config when not in a subsession; once content exists we rely on these
// categories (matching BOX_ORDER) rather than the initial placeholder tabs
export const DEFAULT_INFO_BOX_CONFIG: Partial<Record<BoxType, ResourceKey[]>> = {
  // FEATURED holds only artifacts (plus the home-page placeholder); nuggets never route to it
  FEATURED: [],
  SUGGESTED: ALL_CONTENT_RESOURCE_KEYS,
  TOOLS: [RESOURCE_KEY.TOOLS, RESOURCE_KEY.SUGGESTED_PLAYBOOKS, RESOURCE_KEY.COMPETENCIES],
  INTERNSHIPS: [RESOURCE_KEY.INTERNSHIPS],
  CAREER_PATHS: [RESOURCE_KEY.CAREER_PATHS],
  SCHOLARSHIPS: [RESOURCE_KEY.SCHOLARSHIPS],
  EMPLOYERS: [RESOURCE_KEY.EMPLOYERS],
  INDUSTRIES: [RESOURCE_KEY.INDUSTRIES],
  COLLEGES: [RESOURCE_KEY.COLLEGES],
  MAJORS: [RESOURCE_KEY.MAJORS],
  LEARNING: [RESOURCE_KEY.STORIES, RESOURCE_KEY.LEARNING],
  COACHES: [RESOURCE_KEY.COACHES, RESOURCE_KEY.DEEP_DIVES],
  // keep placeholder tabs for empty state without letting them collect content later
  RESOURCES: [],
  OPPORTUNITIES: [],
  PROFILES: [],
};

const SUBSESSION_BOX_ORDER = [
  'FEATURED',
  'TOOLS',
  'COACHES',
  'INTERNSHIPS',
  'CAREER_PATHS',
  'SCHOLARSHIPS',
  'EMPLOYERS',
  'INDUSTRIES',
  'COLLEGES',
  'MAJORS',
  'LEARNING',
  'SUGGESTED',
] as const;

const MAJORS_SESSION_BOX_ORDER = [
  'FEATURED',
  'MAJORS',
  'TOOLS',
  'COACHES',
  'INTERNSHIPS',
  'CAREER_PATHS',
  'SCHOLARSHIPS',
  'EMPLOYERS',
  'INDUSTRIES',
  'COLLEGES',
  'LEARNING',
  'RESOURCES',
  'OPPORTUNITIES',
  'PROFILES',
  'SUGGESTED',
] as const;

// base config that applies to all subsessions
const BASE_SUBSESSION_CONFIG: Partial<Record<BoxType, ResourceKey[]>> = {
  // FEATURED is a default-tab placeholder (home empty state) until content arrives; holds no nuggets
  FEATURED: [],
  TOOLS: [RESOURCE_KEY.TOOLS, RESOURCE_KEY.SUGGESTED_PLAYBOOKS, RESOURCE_KEY.COMPETENCIES],
  LEARNING: [RESOURCE_KEY.STORIES, RESOURCE_KEY.LEARNING],
  CAREER_PATHS: [RESOURCE_KEY.CAREER_PATHS],
  INTERNSHIPS: [RESOURCE_KEY.INTERNSHIPS],
  SCHOLARSHIPS: [RESOURCE_KEY.SCHOLARSHIPS],
  EMPLOYERS: [RESOURCE_KEY.EMPLOYERS],
  COLLEGES: [RESOURCE_KEY.COLLEGES],
  MAJORS: [RESOURCE_KEY.MAJORS],
  INDUSTRIES: [RESOURCE_KEY.INDUSTRIES],
  COACHES: [RESOURCE_KEY.COACHES, RESOURCE_KEY.DEEP_DIVES],
  // DEEP_DIVES content routes to COACHES tab via resourceKeyToTab; empty entry preserves BoxType union
  DEEP_DIVES: [],
};

// specify which resources are featured for each subsession
export const SUBSESSION_FEATURED_RESOURCES: Record<LubavOfficeHoursSubsessionType, ResourceKey[]> =
  {
    [OFFICE_HOURS_SUBSESSION_IDS.HIGH_SCHOOL_ACT_SAT]: [
      RESOURCE_KEY.TOOLS,
      RESOURCE_KEY.COMPETENCIES,
      RESOURCE_KEY.SUGGESTED_PLAYBOOKS,
    ],
    [OFFICE_HOURS_SUBSESSION_IDS.HIGH_SCHOOL_COLLEGE_MAJORS]: [RESOURCE_KEY.MAJORS],
    [OFFICE_HOURS_SUBSESSION_IDS.HIGH_SCHOOL_UNIVERSITIES]: [RESOURCE_KEY.COLLEGES],
    [OFFICE_HOURS_SUBSESSION_IDS.HIGH_SCHOOL_SCHOLARSHIP]: [RESOURCE_KEY.SCHOLARSHIPS],
    [OFFICE_HOURS_SUBSESSION_IDS.HIGH_SCHOOL_GRADES]: [
      RESOURCE_KEY.TOOLS,
      RESOURCE_KEY.COMPETENCIES,
      RESOURCE_KEY.SUGGESTED_PLAYBOOKS,
    ],
    [OFFICE_HOURS_SUBSESSION_IDS.HIGH_SCHOOL_RESUME]: [
      RESOURCE_KEY.TOOLS,
      RESOURCE_KEY.COMPETENCIES,
      RESOURCE_KEY.SUGGESTED_PLAYBOOKS,
    ],
    [OFFICE_HOURS_SUBSESSION_IDS.HIGH_SCHOOL_CAREERS]: [RESOURCE_KEY.CAREER_PATHS],
    [OFFICE_HOURS_SUBSESSION_IDS.COLLEGE_INTERNSHIPS]: [RESOURCE_KEY.INTERNSHIPS],
    [OFFICE_HOURS_SUBSESSION_IDS.COLLEGE_GRADES]: [
      RESOURCE_KEY.TOOLS,
      RESOURCE_KEY.COMPETENCIES,
      RESOURCE_KEY.SUGGESTED_PLAYBOOKS,
    ],
    [OFFICE_HOURS_SUBSESSION_IDS.COLLEGE_RESUME]: [
      RESOURCE_KEY.TOOLS,
      RESOURCE_KEY.COMPETENCIES,
      RESOURCE_KEY.SUGGESTED_PLAYBOOKS,
    ],
    [OFFICE_HOURS_SUBSESSION_IDS.COLLEGE_CAREERS]: [RESOURCE_KEY.CAREER_PATHS],
    [OFFICE_HOURS_SUBSESSION_IDS.COLLEGE_MAJORS]: [RESOURCE_KEY.MAJORS],
    [OFFICE_HOURS_SUBSESSION_IDS.COLLEGE_SCHOLARSHIP]: [RESOURCE_KEY.SCHOLARSHIPS],
    [OFFICE_HOURS_SUBSESSION_IDS.GUEST_CAREERS]: [RESOURCE_KEY.CAREER_PATHS],
    [OFFICE_HOURS_SUBSESSION_IDS.GUEST_MAJORS]: [RESOURCE_KEY.MAJORS],
    [OFFICE_HOURS_SUBSESSION_IDS.GUEST_COLLEGES]: [RESOURCE_KEY.COLLEGES],
    [OFFICE_HOURS_SUBSESSION_IDS.GUEST_RESUME]: [
      RESOURCE_KEY.TOOLS,
      RESOURCE_KEY.COMPETENCIES,
      RESOURCE_KEY.SUGGESTED_PLAYBOOKS,
    ],
    [OFFICE_HOURS_SUBSESSION_IDS.GUEST_GRADES]: [
      RESOURCE_KEY.TOOLS,
      RESOURCE_KEY.COMPETENCIES,
      RESOURCE_KEY.SUGGESTED_PLAYBOOKS,
    ],
    [OFFICE_HOURS_SUBSESSION_IDS.GUEST_INTERNSHIPS]: [RESOURCE_KEY.INTERNSHIPS],
    [OFFICE_HOURS_SUBSESSION_IDS.GUEST_SCHOLARSHIP]: [RESOURCE_KEY.SCHOLARSHIPS],
  };

export const SUBSESSION_CONFIGS: Record<LubavOfficeHoursSubsessionType, SubsessionConfig> =
  Object.fromEntries(
    Object.keys(SUBSESSION_FEATURED_RESOURCES).map((subsessionId) => [
      subsessionId,
      {
        ...BASE_SUBSESSION_CONFIG,
        SUGGESTED: ALL_CONTENT_RESOURCE_KEYS,
      },
    ]),
  ) as Record<LubavOfficeHoursSubsessionType, SubsessionConfig>;

// For a nugget-output session, FEATURED holds the session's main nugget types and the named
// tabs that would otherwise only repeat them are dropped so they aren't duplicated.
const nuggetOutputBoxConfig = (
  nuggetOutputKeys: ResourceKey[],
): Partial<Record<BoxType, ResourceKey[]>> => {
  const claimed = new Set<ResourceKey>(nuggetOutputKeys);
  const ownedByFeatured = ([boxType, keys]: [BoxType, ResourceKey[]]): boolean =>
    boxType !== 'SUGGESTED' && keys.length > 0 && keys.every((key) => claimed.has(key));

  return {
    ...Object.fromEntries(
      (Object.entries(DEFAULT_INFO_BOX_CONFIG) as [BoxType, ResourceKey[]][]).filter(
        (entry) => !ownedByFeatured(entry),
      ),
    ),
    FEATURED: nuggetOutputKeys,
  };
};

export const getInfoBoxConfig = (
  subsessionId: LubavOfficeHoursSubsessionType | null,
  activeSessionId?: string | null,
): InfoBoxConfigMap => {
  const nuggetOutputKeys = getNuggetOutputResources(activeSessionId);
  const config =
    subsessionId && subsessionId in SUBSESSION_CONFIGS
      ? SUBSESSION_CONFIGS[subsessionId]
      : nuggetOutputKeys.length > 0
        ? nuggetOutputBoxConfig(nuggetOutputKeys)
        : DEFAULT_INFO_BOX_CONFIG;

  return Object.fromEntries(
    Object.entries(config)
      .filter(([, boxConfig]) => boxConfig)
      .map(([boxType, boxConfig]) => [
        boxType,
        {
          title: CATEGORY_TITLE_MAP[boxType as BoxType],
          resources: [...boxConfig],
        },
      ]),
  ) as InfoBoxConfigMap;
};

// note: we can remove this once we update supercoach to use the same tabs as subsessions
export const getBoxOrder = (
  subsessionId: LubavOfficeHoursSubsessionType | null,
  activeSessionId?: string | null,
): readonly BoxType[] => {
  if (!subsessionId) {
    if (activeSessionId === OFFICE_HOURS_SESSION_IDS.MAJORS) {
      return MAJORS_SESSION_BOX_ORDER;
    }

    return BOX_ORDER;
  }

  if (subsessionId in SUBSESSION_CONFIGS) {
    return SUBSESSION_BOX_ORDER;
  }

  return BOX_ORDER;
};

export const getDefaultTabs = (subsessionId: LubavOfficeHoursSubsessionType | null): BoxType[] => {
  const sortTabs = (tabs: BoxType[], order: readonly BoxType[]): BoxType[] =>
    order.filter((boxType) => tabs.includes(boxType));

  if (!subsessionId) {
    return sortTabs(['FEATURED', 'TOOLS', 'COACHES'], BOX_ORDER);
  }

  // placeholders for the boxes the subsession's featured content will land in, plus
  // SUGGESTED, with FEATURED shown until content arrives
  const featuredBoxes = SUBSESSION_FEATURED_RESOURCES[subsessionId]
    .map(
      (resource) =>
        (Object.entries(BASE_SUBSESSION_CONFIG) as [BoxType, ResourceKey[]][]).find(([, keys]) =>
          keys.includes(resource),
        )?.[0],
    )
    .filter((boxType): boxType is BoxType => boxType !== undefined);

  return sortTabs(['FEATURED', ...featuredBoxes, 'SUGGESTED'], SUBSESSION_BOX_ORDER);
};
